import isNull from 'lodash/isNull';

import { getUniqChunkedIds } from '@utils/getUniqChunkedIds';

import {
    CalculateCostParams,
    CalculateCostResponse,
    CreateDiagnosisParams,
    CreateEntryResponse,
    DentalTreatmentParams,
    EntryCreateForm,
    EntryArchiveExportResponse,
    EntryArchiveExportStatusResponse,
    EntryDataUpdateParams,
    EntryData,
    EntryDetailData,
    EntryDetailResponse,
    EntryEDSData,
    EntryEDSResponse,
    EntryListItem,
    EntryListQuery,
    EntryReservationQuery,
    EntryReservationResponse,
    EntryTypeListItemData,
    EntryVersionData,
    EntryVersionResponse,
    EntryWithRelationsResponse,
    FileViewModel,
    InsuranceApprovalHistoryVersion,
    Optional,
    RenderedServiceCreateForm,
    RenderedServiceContractorUpdateForm,
    RenderedServiceUpdateForm,
    RenderedServiceUpdateStatusForm,
    ReservationData,
    ResolvedEntryTypeQuery,
    ResolvedEntryTypeResponse,
    UpdateDiagnosisParams,
    VisitTimeUpdateParams,
    EntryListResponse,
    EntryRenderedServiceStatisticsData,
} from 'chaika-frontend-types/frontend-cms';
import { Currency, EhrViewContext, EHR_VIEW_CONTEXT_HEADER, EntryStatus } from 'chaika-enums';

import { axios } from '@lib/axios';

import { removeNilKeys } from '@utils/removeNiKeys';
import type {
    EntryCertificateCreationParams,
    EntryExistsResponse,
    FieldsUpdateParams,
    FileCreationBody,
} from 'chaika-types';
import { DateHelper } from '@utils/DateHelper';

const BASE_URL = '/api/entry';

export class EntryApi {
    public static async startArchiveExport(ehrId: string): Promise<EntryArchiveExportResponse> {
        const res = await axios.post<EntryArchiveExportResponse>(`${BASE_URL}/archive`, { ehrId });

        return res.data;
    }

    public static async getArchiveExportStatus(exportId: string): Promise<EntryArchiveExportStatusResponse> {
        const res = await axios.get<EntryArchiveExportStatusResponse>(`${BASE_URL}/archive/${exportId}`);

        return res.data;
    }

    public static async create(entry: EntryCreateForm): Promise<CreateEntryResponse> {
        const res = await axios.post<CreateEntryResponse>(`${BASE_URL}`, { entry });

        return res.data;
    }

    public static async findReservations(params: EntryReservationQuery): Promise<EntryReservationResponse> {
        const res = await axios.get<EntryReservationResponse>(`${BASE_URL}/reservation`, { params });

        return res.data;
    }

    public static async getResolvedType(params: ResolvedEntryTypeQuery): Promise<EntryTypeListItemData | null> {
        const res = await axios.get<ResolvedEntryTypeResponse>(`${BASE_URL}/resolved-type`, { params });

        return res.data.entryType;
    }

    public static async setEntryReservation(entryId: string, reservation: ReservationData): Promise<void> {
        await axios.post(`${BASE_URL}/${entryId}/reservation`, { reservation });
    }

    public static async removeEntryReservation(
        entryId: string,
        reservationId: string,
        referralId?: string | null,
    ): Promise<void> {
        await axios.delete(`${BASE_URL}/${entryId}/reservation/${reservationId}`, { params: { referralId } });
    }

    public static async getList(query?: EntryListQuery): Promise<EntryListItem[]> {
        const res = await axios.get<EntryListResponse>(`${BASE_URL}`, { params: { ...query } });

        return res.data.entries;
    }

    public static async getListWithCount(params?: EntryListQuery): Promise<EntryListResponse> {
        const res = await axios.get<EntryListResponse>(`${BASE_URL}`, { params });

        return res.data;
    }

    public static async exists(params: EntryListQuery): Promise<boolean> {
        const res = await axios.get<EntryExistsResponse>(`${BASE_URL}/exists`, { params });

        return res.data.exists;
    }

    public static async getEntriesDetailsList(ids: string[]): Promise<EntryDetailData[]> {
        let res;
        try {
            res = await axios.get<EntryDetailResponse>(`${BASE_URL}/detail`, { params: { ids: ids.join(',') } });
        } catch (e: any) {
            console.error(e);
        }

        return !!res ? res.data.entries : [];
    }

    public static async getEntry(id: string, viewContext?: EhrViewContext): Promise<EntryWithRelationsResponse> {
        const headers = viewContext ? { [EHR_VIEW_CONTEXT_HEADER]: viewContext } : undefined;
        const res = await axios.get<EntryWithRelationsResponse>(`${BASE_URL}/${id}`, {
            headers,
            skipForbiddenPopup: true,
        });

        return res.data;
    }

    public static async getEntryWithoutRelations(id: string): Promise<EntryData> {
        const res = await axios.get<EntryWithRelationsResponse>(`${BASE_URL}/${id}`, { skipForbiddenPopup: true });

        return res.data.entry;
    }

    public static async removeEntry(id: string): Promise<void> {
        await axios.delete(`${BASE_URL}/${id}`);
    }

    public static async getEntryVersion(id: string): Promise<EntryVersionData[]> {
        const res = await axios.get<EntryVersionResponse>(`${BASE_URL}/${id}/version`, { skipForbiddenPopup: true });

        return res.data.versions;
    }

    public static async getInsuranceApprovalHistory(id: string): Promise<InsuranceApprovalHistoryVersion[]> {
        const res = await axios.get(`${BASE_URL}/${id}/insurance-approval/history`, { skipForbiddenPopup: true });

        return res.data.versions;
    }

    public static async updateDoctor(id: string, doctorId: string): Promise<void> {
        await axios.put(`${BASE_URL}/${id}/doctor`, { doctor: { id: doctorId } });
    }

    public static async updateData(id: string, entry: EntryDataUpdateParams): Promise<void> {
        await axios.put(`${BASE_URL}/${id}`, { entry });
    }

    public static async checkoutVersions(id: string, time: string, ehrId: string): Promise<void> {
        await axios.put(`${BASE_URL}/${id}/version/${time}`, { ehrId });
    }

    public static async updateStatus(id: string, status: EntryStatus): Promise<void> {
        await axios.put(`${BASE_URL}/${id}/status`, { status });
    }

    public static async updateUrgency(id: string, isUrgent: boolean): Promise<void> {
        await axios.put(`${BASE_URL}/${id}/urgency`, { isUrgent });
    }

    public static async updateNote(id: string, note: string): Promise<void> {
        await axios.put(`${BASE_URL}/${id}/note`, { note });
    }

    public static async updateInsuranceApproval(id: string, insuranceApproval: string): Promise<void> {
        await axios.put(`${BASE_URL}/${id}/insurance-approval`, { insuranceApproval });
    }

    public static async updateType(id: string, typeId: string): Promise<void> {
        await axios.put(`${BASE_URL}/${id}/type`, { typeId });
    }

    public static async updateField(id: string, fields: FieldsUpdateParams[], ehrId: string): Promise<void> {
        await axios.put(`${BASE_URL}/${id}/field`, {
            ehrId,
            fields,
            updatedAt: DateHelper.now().toISOString(),
        });
    }

    public static async createMedicalCase(entryId: string, value: CreateDiagnosisParams, ehrId: string): Promise<void> {
        await axios.post(`${BASE_URL}/${entryId}/field/diagnosis`, { value, ehrId });
    }

    public static async updateMedicalCase(entryId: string, value: UpdateDiagnosisParams, ehrId: string): Promise<void> {
        await axios.put(`${BASE_URL}/${entryId}/field/diagnosis`, { value, ehrId });
    }

    public static async updateDentalTreatmentField(
        entryId: string,
        dentalTreatments: DentalTreatmentParams,
    ): Promise<void> {
        await axios.put(`${BASE_URL}/${entryId}/field/dental-treatment`, { dentalTreatments });
    }

    public static async executeReferrals(entryId: string, ids: string[]): Promise<void> {
        await axios.post(`${BASE_URL}/${entryId}/referral-execution`, { ids });
    }

    public static async removeReferrals(entryId: string, ids: string[]): Promise<void> {
        await axios.delete(`${BASE_URL}/${entryId}/referral-execution`, { params: { ids: ids.join(',') } });
    }

    public static async createRenderedService(entryId: string, createForm: RenderedServiceCreateForm): Promise<void> {
        await axios.post(`${BASE_URL}/${entryId}/field/rendered-service`, createForm);
    }

    public static async updateRenderedService(
        entryId: string,
        { ids, renderedService }: RenderedServiceUpdateForm,
    ): Promise<void> {
        await axios.put(`${BASE_URL}/${entryId}/field/rendered-service`, {
            ids,
            renderedService: removeNilKeys(renderedService),
        });
    }

    public static async updateRenderedServiceContractor(
        entryId: string,
        contractorUpdateForm: RenderedServiceContractorUpdateForm,
    ): Promise<void> {
        await axios.put(`${BASE_URL}/${entryId}/field/rendered-service/contractor`, contractorUpdateForm);
    }

    public static async updateRenderedServiceStatus(
        entryId: string,
        updateStatusForm: RenderedServiceUpdateStatusForm,
    ): Promise<void> {
        await axios.put(`${BASE_URL}/${entryId}/field/rendered-service/status`, updateStatusForm);
    }

    public static async removeRenderedService(entryId: string, ids: string[]): Promise<void> {
        const chunked = getUniqChunkedIds(ids);

        await Promise.all(
            chunked.map(ids =>
                axios.delete(`${BASE_URL}/${entryId}/field/rendered-service/`, { params: { ids: ids.join(',') } }),
            ),
        );
    }

    public static async calculateRenderedService(params: CalculateCostParams): Promise<CalculateCostResponse> {
        try {
            const organizationId = isNull(params.organizationId) ? undefined : params.organizationId;
            const userId = isNull(params.userId) ? undefined : params.userId;
            const { data } = await axios.post('/api/cost', { ...params, organizationId, userId });

            return data;
        } catch (e: any) {
            console.error('Error on calculateRenderedService:', e.message);
            return {
                amountToPay: 0,
                totalCost: 0,
                currency: Currency.RUB,
            };
        }
    }

    public static async addFile(entryId: string, params: FileCreationBody): Promise<FileViewModel> {
        const url = `${BASE_URL}/${entryId}/file`;
        const { data } = await axios.post(url, params);

        return data;
    }

    public static async setFileUploadedStatus(entryId: string, fileName: string): Promise<void> {
        const url = `${BASE_URL}/${entryId}/file/${fileName}/status/uploaded`;
        await axios.put(url);
    }

    public static async deleteFile(entryId: string, fileName: string): Promise<void> {
        const url = `${BASE_URL}/${entryId}/file/${fileName}`;

        await axios.delete(url);
    }

    public static async addCoAuthor(entryId: string): Promise<void> {
        const url = `${BASE_URL}/${entryId}/coauthor`;
        await axios.post(url);
    }

    public static async changeCoAuthors(entryId: string, coAuthorIds: string[]): Promise<void> {
        await axios.put(`${BASE_URL}/${entryId}/coauthor`, { coAuthorIds });
    }

    public static async editVisitTime(entryId: string, visitTime: VisitTimeUpdateParams): Promise<void> {
        const url = `${BASE_URL}/${entryId}/visitTime`;
        await axios.put(url, { visitTime });
    }

    public static async saveReport(entryId: string, formData: FormData): Promise<void> {
        await axios.post(`${BASE_URL}/${entryId}/report`, formData);
    }

    public static async signWithEds(entryId: string, certificate: EntryCertificateCreationParams): Promise<void> {
        await axios.post(`${BASE_URL}/${entryId}/eds`, certificate);
    }

    public static async deleteEds(entryId: string): Promise<void> {
        await axios.delete(`${BASE_URL}/${entryId}/eds`);
    }

    public static async getEntryEDS(entryId: string): Promise<Optional<EntryEDSData>> {
        const res = await axios.get<EntryEDSResponse>(`${BASE_URL}/${entryId}/eds`, { skipForbiddenPopup: true });
        return res.data.certificate;
    }

    public static async getStatistic(entryIds: string[]): Promise<EntryRenderedServiceStatisticsData[]> {
        const res = await axios.get<EntryRenderedServiceStatisticsData[]>(`${BASE_URL}/rendered-service/statistic`, {
            params: { entryId: entryIds.join() },
        });

        return res.data;
    }
}

export { BASE_URL as ENTRY_BASE_URL };
